import { useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { api, unwrap } from '@/api/client';
import { orgKey } from '@/app/queryClient';
import { useOrg, useOrgContext } from '@/app/session';
import { Button, EmptyState, ErrorState, LoadingState, Section, Timeline } from '@/components/ui';
import { HistoricalNoteContent, type HistoricalNote } from './HistoricalNoteContent';

type Target = { companyId: string; locationId?: never } | { locationId: string; companyId?: never };

/** Session UI for private archives; API also supports eligible owner-backed migration keys. */
export function HistoricalNotesSection(props: Target) {
  const { user } = useOrgContext();
  const org = useOrg();
  const allowed = Boolean(user) && (org.hasScope('audit:content') || org.hasScope('migration:read')) && org.hasScope('parties:read') && org.hasScope('locations:read');
  if (!allowed) return null;
  return <History key={`${org.orgId}:${props.companyId ?? props.locationId}`} {...props} />;
}

function History(target: Target) {
  const { orgId, isAdmin, hasScope } = useOrg();
  const sensitiveAllowed = isAdmin && hasScope('migration:read') && hasScope('audit:content');
  const q = useInfiniteQuery({
    queryKey: orgKey(orgId, 'historical-notes', target.companyId ?? '', target.locationId ?? ''),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ signal, pageParam }) => unwrap(api.GET('/api/v1/orgs/{orgId}/historical-notes', {
      params: { path: { orgId }, query: { company_id: target.companyId, location_id: target.locationId, limit: 25, cursor: pageParam } }, signal,
    })),
    getNextPageParam: (last) => last.page.has_more ? last.page.next_cursor ?? undefined : undefined,
  });
  // Concurrent imports can move a logical note between pages. Keep the highest
  // captured version if it occurs twice, as documented by the API paging policy.
  const notes = new Map<string, HistoricalNote>();
  for (const n of q.data?.pages.flatMap(p => p.items) ?? []) {
    const prior = notes.get(n.id);
    if (!prior || prior.version < n.version) notes.set(n.id, n);
  }
  return <Section id="historical-notes" title="Historical notes">
    {q.isPending ? <LoadingState compact label="Loading historical notes" /> : q.isError && !q.data ? <ErrorState error={q.error} onRetry={() => void q.refetch()} /> : notes.size === 0 ? <EmptyState compact title="No historical notes have been projected here." /> : <div className="stack">
      <Timeline label="Historical notes, occurred or source creation time, newest first" entries={[...notes.values()].map(n => ({
        id: n.id, at: n.occurred_at ?? n.source_created_at,
        title: n.tombstone ? 'Historical note · deleted' : 'Historical note',
        body: <div className="stack"><HistoricalNoteContent note={n} humanAllowed />{sensitiveAllowed ? <CapturedEvidence key={`${n.id}:${n.version}`} note={n} /> : null}</div>,
      }))} />
      {q.isError ? <ErrorState error={q.error} onRetry={() => void (q.isFetchNextPageError ? q.fetchNextPage() : q.refetch())} /> : null}
      {q.hasNextPage ? <Button size="sm" variant="quiet" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>Show older notes</Button> : null}
    </div>}
  </Section>;
}

function CapturedEvidence({ note }: { note: HistoricalNote }) {
  const { orgId } = useOrg();
  const [selection, setSelection] = useState<{ version: number; sensitive: boolean } | null>(null);
  const q = useQuery({
    queryKey: orgKey(orgId, 'historical-note-evidence', note.id, selection?.version ?? '', selection?.sensitive ?? false),
    enabled: selection !== null,
    staleTime: 0,
    gcTime: 0,
    queryFn: ({ signal }) => unwrap(api.GET('/api/v1/orgs/{orgId}/historical-notes/{noteId}', {
      params: { path: { orgId, noteId: note.id }, query: { captured_version: selection?.version, sensitive: selection?.sensitive } }, signal,
    })),
  });
  return <div className="stack">
    <Button size="sm" variant="quiet" onClick={() => setSelection({ version: note.version, sensitive: true })}>Reveal retained original / deleted content</Button>
    {note.version > 1 ? <label>Captured import version <select aria-label="Captured import version" value={selection?.version ?? ''} onChange={e => setSelection(e.target.value ? { version: Number(e.target.value), sensitive: false } : null)}>
      <option value="">Choose a capture</option>
      {Array.from({ length: note.version }, (_, i) => i + 1).reverse().map(v => <option key={v} value={v}>Capture {v}</option>)}
    </select></label> : null}
    {selection ? <div className="stack">
      <Button size="sm" variant="quiet" onClick={() => setSelection(null)}>Hide captured evidence</Button>
      {q.isPending ? <LoadingState compact label="Loading captured evidence" /> : q.isError ? <ErrorState error={q.error} onRetry={() => void q.refetch()} /> : <HistoricalNoteContent note={q.data} humanAllowed />}
    </div> : null}
  </div>;
}
